import React, { useMemo } from 'react';
import { Line, Text } from '@react-three/drei';
import * as THREE from 'three';

// Grid-Konfiguration
const GRID_SIZE = 50000; // 50m in jede Richtung
const MINOR_STEP = 1000;  // 1m kleine Linien
const MAJOR_STEP = 5000;  // 5m große Linien

export function Grid3D({ showAxes = true, showLabels = true, opacity = 0.3, viewMode = '2d' }) {
  // Grid-Linien generieren
  const { minorLines, majorLines } = useMemo(() => {
    const minor = [];
    const major = [];

    // Vertikale Linien (X-Richtung)
    for (let x = -GRID_SIZE; x <= GRID_SIZE; x += MINOR_STEP) {
      const isMajor = x % MAJOR_STEP === 0;
      const target = isMajor ? major : minor;
      target.push([
        new THREE.Vector3(x, -GRID_SIZE, 0),
        new THREE.Vector3(x, GRID_SIZE, 0),
      ]);
    }

    // Horizontale Linien (Y-Richtung)
    for (let y = -GRID_SIZE; y <= GRID_SIZE; y += MINOR_STEP) {
      const isMajor = y % MAJOR_STEP === 0;
      const target = isMajor ? major : minor;
      target.push([
        new THREE.Vector3(-GRID_SIZE, y, 0),
        new THREE.Vector3(GRID_SIZE, y, 0),
      ]);
    }

    return { minorLines: minor, majorLines: major };
  }, []);

  // Achsen-Beschriftungen
  const axisLabels = useMemo(() => {
    const labels = [];
    for (let i = MAJOR_STEP; i <= GRID_SIZE; i += MAJOR_STEP) {
      // X-Achse (rechts)
      labels.push({ pos: [i, -300, 1], text: `${i / 1000}m` });
      // X-Achse (links)
      labels.push({ pos: [-i, -300, 1], text: `-${i / 1000}m` });
      // Y-Achse (oben)
      labels.push({ pos: [-400, i, 1], text: `${i / 1000}m` });
      // Y-Achse (unten)
      labels.push({ pos: [-400, -i, 1], text: `-${i / 1000}m` });
    }
    return labels;
  }, []);

  return (
    <group>
      {/* Minor Grid Lines (1m) */}
      {minorLines.map((points, i) => (
        <Line
          key={`minor-${i}`}
          points={points}
          color="#e5e7eb"
          lineWidth={0.5}
        />
      ))}

      {/* Major Grid Lines (5m) */}
      {majorLines.map((points, i) => (
        <Line
          key={`major-${i}`}
          points={points}
          color="#d1d5db"
          lineWidth={1}
        />
      ))}

      {/* Koordinaten-Achsen */}
      {showAxes && (
        <>
          {/* X-Achse (rot) */}
          <Line
            points={[
              new THREE.Vector3(-GRID_SIZE, 0, 1),
              new THREE.Vector3(GRID_SIZE, 0, 1),
            ]}
            color="#ef4444"
            lineWidth={2}
          />
          {/* Pfeilspitze X */}
          <mesh position={[GRID_SIZE - 200, 0, 1]}>
            <coneGeometry args={[100, 300, 8]} />
            <meshBasicMaterial color="#ef4444" />
          </mesh>

          {/* Y-Achse (grün) */}
          <Line
            points={[
              new THREE.Vector3(0, -GRID_SIZE, 1),
              new THREE.Vector3(0, GRID_SIZE, 1),
            ]}
            color="#22c55e"
            lineWidth={2}
          />
          {/* Pfeilspitze Y */}
          <mesh position={[0, GRID_SIZE - 200, 1]} rotation={[0, 0, Math.PI / 2]}>
            <coneGeometry args={[100, 300, 8]} />
            <meshBasicMaterial color="#22c55e" />
          </mesh>

          {/* Z-Achse (blau) - nur in 3D-Ansicht */}
          {viewMode === '3d' && (
            <>
              <Line
                points={[
                  new THREE.Vector3(0, 0, 0),
                  new THREE.Vector3(0, 0, 5000), // 5m hoch
                ]}
                color="#3b82f6"
                lineWidth={2}
              />
              {/* Pfeilspitze Z (nach oben zeigend) */}
              <mesh position={[0, 0, 4800]} rotation={[Math.PI / 2, 0, 0]}>
                <coneGeometry args={[100, 300, 8]} />
                <meshBasicMaterial color="#3b82f6" />
              </mesh>
              {/* Z-Beschriftung */}
              <Text
                position={[200, 0, 5000]}
                fontSize={150}
                color="#3b82f6"
                anchorX="left"
                anchorY="middle"
              >
                Z
              </Text>
            </>
          )}

          {/* Ursprung markieren */}
          <mesh position={[0, 0, viewMode === '3d' ? 0 : 0.5]}>
            <sphereGeometry args={[viewMode === '3d' ? 100 : 80, 32, 16]} />
            <meshBasicMaterial color="#374151" />
          </mesh>
        </>
      )}

      {/* Achsen-Beschriftungen */}
      {showLabels && axisLabels.map((label, i) => (
        <Text
          key={`label-${i}`}
          position={label.pos}
          fontSize={150}
          color="#9ca3af"
          anchorX="center"
          anchorY="middle"
        >
          {label.text}
        </Text>
      ))}
    </group>
  );
}
